import 'core-js/stable'; //ie11 polyfill
import 'whatwg-fetch'; // ie11 polyfill

// It is recommended to write this registration code in a separated file because you have to register them before any component definitions. You can make sure the execution order by putting import statement for the hooks registration on the top of the main file:
// https://class-component.vuejs.org/guide/additional-hooks.html
import './class-component-hooks';

import * as globals from '@/globals';

// vue standard imports
import Vue from 'vue';
import VueGtag from 'vue-gtag';
import App from './App.vue';
import router from './router';
import store from './store';

// global components we want to use everywhere
import List from '@/components/util/List.vue';
import ActivitySpinner from '@/components/util/ActivitySpinner.vue';

import JobTypePicker from '@/components/system/JobTypePicker.vue';
import TradePicker from '@/components/system/TradePicker.vue';
import JobPriorityPicker from '@/components/system/JobPriorityPicker.vue';
import WorkerPicker from '@/components/workers/WorkerPicker.vue';
import WorkerReassignPicker from '@/components/system/WorkerReassignPicker.vue';
import PropertyPicker from '@/components/system/PropertyPicker.vue';
import CampusPicker from '@/components/system/CampusPicker.vue';
import Location1Picker from '@/components/system/Location1Picker.vue';
import ContactPicker from '@/components/system/ContactPicker.vue';
import TippyPopover from '@/components/util/TippyPopover.vue';
import WorkOrderListView from '@/components/orders/WorkOrderListView.vue';

import ValidationErrors from '@/components/system/ValidationErrors.vue';

import DisplayField from '@/components/util/DisplayField.vue';
import Modal from '@/components/util/Modal.vue';

import CompleteOrderComponent from '@/components/orders/CompleteOrderComponent.vue';

import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

import VuePortal from '@linusborg/vue-simple-portal';

const GoogleMap = () => import('@/components/system/GoogleMap.vue');
const BarcodeScanner = () => import('@/components/system/BarcodeScanner.vue');

import { library } from '@fortawesome/fontawesome-svg-core';

import {
  faBell,
  faCloudUploadAlt,
  faUserEdit,
  faUser,
  faSignOutAlt,
  faTimes,
  faFilePdf,
  faCog,
  faLessThan,
  faHome,
  faHamburger,
  faWrench,
  faTag,
  faBars,
  faIdCard,
  faAddressCard,
  faChartLine,
  faFile,
  faCalendar,
  faAddressBook,
  faCubes,
  faClipboardCheck,
  faSortAmountDownAlt,
  faSortAmountDown,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faPaperPlane,
  faPlus,
  faSearch,
  faBarcode,
  faTrash,
  faFilter,
  faCaretDown,
  faSquare,
  faCheckSquare,
  faRedo,
  faExclamationCircle,
  faEllipsisV,
  faPrint,
  faChevronDown,
  faCircle,
  faChevronLeft,
  faChevronRight,
  faThermometerHalf,
  faClock,
  faEdit,
  faLink,
  faStepForward,
  faStepBackward,
  faExpandAlt,
  faExternalLinkAlt,
  faCheck,
  faChevronUp,
  faQuestionCircle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { faSquare as farSquare } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import vuetify from './plugins/vuetify';

if ('serviceWorker' in navigator) {
  if (process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered: ', registration);
        })
        .catch((registrationError) => {
          console.log(
            'Service Worker registration failed: ',
            registrationError
          );
        });
    });
  } else {
    console.log('non-production no service worker registered');
  }
}

library.add(
  faBell,
  faCloudUploadAlt,
  faUserEdit,
  faUser,
  faSignOutAlt,
  faTimes,
  faFilePdf,
  faCog,
  faLessThan,
  faHome,
  faHamburger,
  faWrench,
  faTag,
  faBars,
  faIdCard,
  faAddressCard,
  faChartLine,
  faFile,
  faCalendar,
  faAddressBook,
  faCubes,
  faClipboardCheck,
  faSortAmountDownAlt,
  faSortAmountDown,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faPaperPlane,
  faPlus,
  faSearch,
  faBarcode,
  faTrash,
  faFilter,
  faCaretDown,
  faSquare,
  faCheckSquare,
  faRedo,
  faExclamationCircle,
  faEllipsisV,
  faPrint,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faThermometerHalf,
  faClock,
  faEdit,
  faLink,
  faStepForward,
  faStepBackward,
  faExternalLinkAlt,
  faExpandAlt,
  faCheck,
  farSquare,
  faExpandAlt,
  faChevronUp,
  faQuestionCircle,
  faCheckCircle
);

Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('list', List);
Vue.component('activity-spinner', ActivitySpinner);

Vue.component('job-type-picker', JobTypePicker);
Vue.component('trade-picker', TradePicker);
Vue.component('job-priority-picker', JobPriorityPicker);
Vue.component('property-picker', PropertyPicker);
Vue.component('worker-picker', WorkerPicker);
Vue.component('worker-reassign-picker', WorkerReassignPicker);
Vue.component('campus-picker', CampusPicker);
Vue.component('location1-picker', Location1Picker);
Vue.component('contact-picker', ContactPicker);
Vue.component('popover', TippyPopover);

// NOTE: <work-order-list-view> is used recursively in the OrderList.vue
// Need to define globally to prevent console error: https://stackoverflow.com/a/58875919/10421823
Vue.component('work-order-list-view', WorkOrderListView);

Vue.component('display-field', DisplayField);
Vue.component('modal', Modal);

Vue.component('validation-errors', ValidationErrors);

Vue.component('complete-order-component', CompleteOrderComponent);

// NOTE: typings appear to not be quite right, so have to do as any to make this happy.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
Vue.component('date-picker', DatePicker as any);

Vue.config.productionTip = false;

Vue.filter('dateTimeFormat', (value: Date) => {
  if (value) {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    };
    return new Intl.DateTimeFormat('en-US', options).format(value);
  }
  return '';
});

Vue.filter('dateFormat', (value: Date) => {
  if (value) {
    return value.toLocaleDateString('en-US', {
      month: '2-digit',
      year: 'numeric',
      day: '2-digit'
    });
  }
  return '';
});
Vue.filter('timeFormat', (value: Date) => {
  if (value) {
    return value.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  }
  return '';
});
Vue.filter('currencyFormat', (value: number) => {
  if (value !== null && value !== undefined) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    });
    return formatter.format(value);
  }
  return '';
});

Vue.use(
  VueGtag,
  {
    config: {
      id: globals.gaTrackerId
    },
    appName: 'Performo',
    pageTrackerScreenviewEnabled: true
  },
  router
);

Vue.use(VuePortal, {
  name: 'portal' // optional, use to rename component
});

Vue.component('GoogleMap', GoogleMap);

Vue.component('BarcodeScanner', BarcodeScanner);

new Vue({
  router,
  store,
  vuetify,
  render: (h) => h(App)
}).$mount('#app');
